import React, { useState, useEffect, memo, useMemo, useCallback, useRef } from "react";
import {
  User,
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { SignOut } from "../SignIn";
import { useTheme } from "../../hooks/ThemeContext";
import { debugFirebaseConfig } from "./DebugFirebaseConfig";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../hooks/ConfigProvider";
import { httpsCallableWithAppId } from "../../service/CallableWrapper";
import { FIREBASE_APP } from "../../Firebase";
import { getRemoteEnv } from "../../config/remoteEnv";

interface LoginProps {
  checkVerify?: boolean;
}

// autoFocus only on keyboard+mouse devices: iOS Safari zooms hard into a
// programmatically focused field on mount (and won't open the keyboard
// for it anyway — that needs a real tap).
const AUTO_FOCUS_OK =
  typeof window === "undefined" || !window.matchMedia?.("(pointer: coarse)")?.matches;

// ── Step 1: Email entry ──────────────────────────────────────────
const EmailForm = memo(
  ({
    email,
    onEmailChange,
    onSubmit,
    loading,
  }: {
    email: string;
    onEmailChange: (v: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    loading: boolean;
  }) => (
    <form onSubmit={onSubmit} style={s.form}>
      <div style={s.fieldGroup}>
        <label style={s.label} className="sl-label">Email</label>
        <input
          className="sl-input"
          type="email"
          placeholder="you@example.com"
          value={email}
          required
          autoComplete="email"
          autoFocus={AUTO_FOCUS_OK}
          onChange={(e) => onEmailChange(e.target.value)}
        />
      </div>
      <button type="submit" className="sl-btn sl-btn-primary" disabled={loading}>
        {loading ? "Sending…" : "Send Code"}
      </button>
    </form>
  )
);

// ── Step 2: OTP entry ────────────────────────────────────────────
const OtpForm = memo(
  ({
    email,
    otp,
    onOtpChange,
    onSubmit,
    onBack,
    onResend,
    loading,
    resendCooldown,
  }: {
    email: string;
    otp: string[];
    onOtpChange: (index: number, value: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    onBack: () => void;
    onResend: () => void;
    loading: boolean;
    resendCooldown: number;
  }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const formRef = useRef<HTMLFormElement | null>(null);

    const handleChange = (index: number, value: string) => {
      if (value.length > 1) {
        const digits = value.replace(/\D/g, "").slice(0, 6).split("");
        digits.forEach((d, i) => onOtpChange(i, d));
        const lastIndex = Math.min(digits.length - 1, 5);
        inputRefs.current[lastIndex]?.focus();
        // Auto-submit if paste fills all 6 digits
        if (digits.length === 6) {
          setTimeout(() => formRef.current?.requestSubmit(), 50);
        }
        return;
      }
      const digit = value.replace(/\D/g, "");
      onOtpChange(index, digit);
      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      // Auto-submit when last digit is entered
      if (digit && index === 5) {
        setTimeout(() => formRef.current?.requestSubmit(), 50);
      }
    };

    const ready = otp.join("").length === 6;

    return (
      <form ref={formRef} onSubmit={onSubmit} style={s.form}>
        <p style={s.subheading} className="sl-subheading">
          We sent a 6-digit code to{" "}
          <strong style={{ color: "#e8e8e8" }}>{email}</strong>. Check your email
          on your phone or another device and enter the code below.
        </p>

        <div style={s.otpRow}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              className="sl-otp-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={digit}
              autoFocus={index === 0}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
            />
          ))}
        </div>

        <button
          type="submit"
          className="sl-btn sl-btn-primary"
          disabled={loading || !ready}
          style={{ opacity: loading || !ready ? 0.5 : 1, cursor: loading || !ready ? "not-allowed" : "pointer" }}
        >
          {loading ? "Verifying…" : "Sign In"}
        </button>

        <div style={s.otpFooter}>
          <span
            className="sl-link"
            style={{ opacity: resendCooldown > 0 ? 0.4 : 1, cursor: resendCooldown > 0 ? "default" : "pointer" }}
            onClick={resendCooldown > 0 ? undefined : onResend}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
          </span>
          <span className="sl-link" onClick={onBack}>
            Change email
          </span>
        </div>
      </form>
    );
  }
);

// ── Already logged-in status card ───────────────────────────────
interface LoggedInStatusProps {
  user: User;
  restaurantId: string | null;
  restaurantName: string | null;
  onGoToAdmin: () => void;
  onSignOut: () => void;
}

const LoggedInStatus = memo(
  ({ user, restaurantId, restaurantName, onGoToAdmin, onSignOut }: LoggedInStatusProps) => (
    <div style={{ ...s.form, alignItems: "center" }}>
      <div style={s.statusBadge} className="sl-status-badge">
        <span style={s.statusDot} />
        Signed in
      </div>

      <div style={s.infoCard}>
        <div style={s.infoRow}>
          <span style={s.infoLabel} className="sl-info-label">Email</span>
          <span style={s.infoValue} className="sl-info-value">{user.email}</span>
        </div>
        {restaurantId && (
          <div style={{ ...s.infoRow, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            <span style={s.infoLabel} className="sl-info-label">Store</span>
            <span style={s.infoValue} className="sl-info-value">
              {restaurantName || restaurantId}
              {restaurantName && (
                <span style={s.infoSubValue}> ({restaurantId})</span>
              )}
            </span>
          </div>
        )}
      </div>

      <button className="sl-btn sl-btn-primary" style={{ width: "100%" }} onClick={onGoToAdmin}>
        Go to Admin Panel
      </button>
      <button
        className="sl-btn sl-btn-ghost"
        style={{ width: "100%", borderColor: "rgba(255,80,80,0.3)", color: "#f87171" }}
        onClick={onSignOut}
      >
        Sign Out
      </button>
    </div>
  )
);

// ── Main component ───────────────────────────────────────────────
const Login: React.FC<LoginProps> = ({ checkVerify = false }) => {
  const navigate = useNavigate();
  const { config } = useConfig();

  const redirectUrl = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("redirect") || "/";
  }, []);

  const [view, setView] = useState<"email" | "otp" | "loggedIn">("email");
  const [errors, setErrors] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [loading, setLoading] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [user, setUser] = useState<User | null>(null);

  const { theme } = useTheme();

  const restaurantInfo = useMemo(() => {
    if (!config?.restaurants) return { id: null, name: null };
    const queryParams = new URLSearchParams(window.location.search);
    const rId =
      queryParams.get("r") || Object.keys(config?.restaurants)[0] || null;
    const rName = rId ? config?.restaurants[rId]?.name ?? null : null;
    return { id: rId, name: rName };
  }, [config]);

  // ── Resend cooldown timer ─────────────────────────────────────
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ── Auth state listener ───────────────────────────────────────
  useEffect(() => {
    const auth = getAuth();
    debugFirebaseConfig();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setView("loggedIn");
      } else {
        setUser(null);
        setView("email");
      }
    });
    return () => unsubscribe();
  }, []);

  // ── Send OTP (shared by initial send and resend) ──────────────
  const callSendOtp = useCallback(async () => {
    setLoading(true);
    setErrors([]);
    try {
      const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region);
      const sendOtp = httpsCallableWithAppId(functions, "sendOtp");
      await sendOtp({ email: email.trim().toLowerCase() });
      setOtp(Array(6).fill(""));
      setView("otp");
      setResendCooldown(30);
    } catch (err: any) {
      setErrors([err?.message || "Failed to send code. Please try again."]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent) => { e.preventDefault(); callSendOtp(); },
    [callSendOtp]
  );

  const handleResend = useCallback(() => { callSendOtp(); }, [callSendOtp]);

  const handleBack = useCallback(() => {
    setView("email");
    setErrors([]);
    setOtp(Array(6).fill(""));
  }, []);

  const handleEmailChange = useCallback((v: string) => setEmail(v), []);

  const handleOtpChange = useCallback((index: number, value: string) => {
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleOtpSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setErrors([]);
      try {
        const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region);
        const verifyOtp = httpsCallableWithAppId(functions, "verifyOtp");
        const result = await verifyOtp({
          email: email.trim().toLowerCase(),
          code: otp.join(""),
        });
        const { customToken } = result.data as { customToken: string };
        await signInWithCustomToken(getAuth(), customToken);
        // onAuthStateChanged handles transition to "loggedIn"
      } catch (err: any) {
        setErrors([err?.message || "Invalid code. Please try again."]);
        setLoading(false);
      }
    },
    [email, otp]
  );

  const handleGoToAdmin = useCallback(() => navigate("/admin"), [navigate]);
  const handleSignOut = useCallback(() => SignOut("/store-login"), []);

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div style={s.page}>
        <div style={s.blobA} />
        <div style={s.blobB} />

        <div style={s.card} className="sl-card">
          {/* Logo */}
          <div style={s.logoRow}>
            <span className="sl-logo" style={{ fontWeight: 800, color: "white", fontSize: "26px", letterSpacing: "-0.5px" }}>
              Dine<span style={{ color: "royalblue" }}>Platform</span>
            </span>
          </div>

          {/* Heading */}
          {view === "email" && (
            <>
              <h1 style={s.heading} className="sl-heading">Store login</h1>
              <p style={s.subheading} className="sl-subheading">Enter your email to receive a sign-in code.</p>
            </>
          )}
          {view === "otp" && (
            <h1 style={s.heading} className="sl-heading">Enter your code</h1>
          )}
          {/* {view === "loggedIn" && (
            <h1 style={s.heading} className="sl-heading">Store Account</h1>
          )} */}

          {/* Views */}
          {view === "email" && (
            <EmailForm
              email={email}
              onEmailChange={handleEmailChange}
              onSubmit={handleEmailSubmit}
              loading={loading}
            />
          )}
          {view === "otp" && (
            <OtpForm
              email={email}
              otp={otp}
              onOtpChange={handleOtpChange}
              onSubmit={handleOtpSubmit}
              onBack={handleBack}
              onResend={handleResend}
              loading={loading}
              resendCooldown={resendCooldown}
            />
          )}
          {view === "loggedIn" && user && (
            <LoggedInStatus
              user={user}
              restaurantId={restaurantInfo.id}
              restaurantName={restaurantInfo.name}
              onGoToAdmin={handleGoToAdmin}
              onSignOut={handleSignOut}
            />
          )}

          {/* Errors */}
          {errors.map((error, index) => (
            <p key={index} style={s.errorText} className="sl-error-text">{error}</p>
          ))}
        </div>
      </div>
    </>
  );
};

/* ─── Static styles ──────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  page: {
    height: "100vh",
    width: "100vw",
    backgroundColor: "#0a0a0a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    position: "fixed",
    top: 0,
    left: 0,
    overflow: "hidden",
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    boxSizing: "border-box",
  },
  blobA: {
    position: "absolute",
    top: "-120px",
    right: "-80px",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  blobB: {
    position: "absolute",
    bottom: "-140px",
    left: "-100px",
    width: "520px",
    height: "520px",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(65,105,225,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "400px",
    backgroundColor: "#141414",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "20px",
    padding: "40px 36px 32px",
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  logoRow: {
    marginBottom: "28px",
    textAlign: "center",
  },
  heading: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#ffffff",
    margin: "0 0 6px 0",
    letterSpacing: "-0.5px",
  },
  subheading: {
    fontSize: "15px",
    color: "#b0b0b0",
    margin: "0 0 24px 0",
    lineHeight: "1.5",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    color: "#d0d0d0",
    fontWeight: 500,
  },
  subheadingInline: {
    fontSize: "14px",
    color: "#999",
    margin: "0",
    lineHeight: "1.5",
  },
  otpRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
  },
  otpFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  errorText: {
    color: "#ff6b6b",
    fontSize: "13px",
    marginTop: "12px",
    textAlign: "center",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "rgba(34,197,94,0.1)",
    border: "1px solid rgba(34,197,94,0.25)",
    color: "#4ade80",
    fontSize: "13px",
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: "999px",
    marginBottom: "4px",
  },
  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    flexShrink: 0,
  },
  infoCard: {
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.07)",
    overflow: "hidden",
    width: "100%",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    gap: "12px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#888",
    fontWeight: 500,
    flexShrink: 0,
  },
  infoValue: {
    fontSize: "13px",
    color: "#ccc",
    fontWeight: 500,
    textAlign: "right",
    wordBreak: "break-all",
  },
  infoSubValue: {
    fontSize: "11px",
    color: "#777",
    fontWeight: 400,
  },
};

/* ─── CSS ────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');

  .sl-btn {
    padding: 13px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    box-sizing: border-box;
  }

  .sl-btn:disabled {
    cursor: not-allowed;
  }

  .sl-btn-primary {
    background: #4169E1;
    color: #ffffff;
    box-shadow: 0 4px 20px rgba(65,105,225,0.3);
    width: 100%;
  }

  .sl-btn-primary:hover:not(:disabled) {
    background: #5578e8;
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(65,105,225,0.4);
  }

  .sl-btn-ghost {
    background: transparent;
    color: #777;
    border: 1px solid rgba(255,255,255,0.08);
    font-weight: 500;
  }

  .sl-btn-ghost:hover:not(:disabled) {
    color: #aaa;
    background: rgba(255,255,255,0.03);
  }

  .sl-input {
    background: #1a1a1a;
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 9px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #e8e8e8;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .sl-input::placeholder { color: #555; }

  .sl-input:focus {
    border-color: rgba(65,105,225,0.5);
    box-shadow: 0 0 0 3px rgba(65,105,225,0.08);
  }

  .sl-otp-input {
    width: 44px;
    height: 52px;
    font-size: 22px;
    font-weight: 700;
    text-align: center;
    border-radius: 9px;
    border: 1px solid rgba(255,255,255,0.09);
    background: #1a1a1a;
    color: #e8e8e8;
    outline: none;
    font-family: 'DM Sans', sans-serif;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .sl-otp-input:focus {
    border-color: rgba(65,105,225,0.5);
    box-shadow: 0 0 0 3px rgba(65,105,225,0.08);
  }

  .sl-link {
    color: royalblue;
    cursor: pointer;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    transition: opacity 0.15s ease;
  }

  .sl-link:hover { opacity: 0.75; }

  @media (min-width: 768px) {
    .sl-card {
      max-width: 480px !important;
      padding: 48px 43px 38px !important;
    }
    .sl-logo {
      font-size: 31px !important;
    }
    .sl-heading {
      font-size: 29px !important;
    }
    .sl-subheading {
      font-size: 18px !important;
    }
    .sl-label {
      font-size: 15px !important;
    }
    .sl-btn {
      font-size: 17px !important;
      padding: 15px 24px !important;
    }
    .sl-input {
      font-size: 17px !important;
      padding: 14px 17px !important;
    }
    .sl-otp-input {
      width: 53px !important;
      height: 62px !important;
      font-size: 26px !important;
    }
    .sl-link {
      font-size: 15px !important;
    }
    .sl-info-label {
      font-size: 15px !important;
    }
    .sl-info-value {
      font-size: 15px !important;
    }
    .sl-status-badge {
      font-size: 15px !important;
    }
    .sl-error-text {
      font-size: 15px !important;
    }
  }
`;


export default Login;